import { useState } from "react";
import { PrimaryButton, SecondaryButton } from "@/components/StandardButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Palette, Upload, Loader2 } from "lucide-react";
import { DisplayM, BodyText } from "@/components/ui/typography";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface BrandingStepProps {
  pageTitle: string;
  initialColor: string;
  initialLogo: string;
  onSave: (color: string, logo: string) => void;
  onSkip: () => void;
}

const COLOR_PRESETS = [
  "#5B72FF", // Primary brand
  "#3b82f6", // Blue
  "#10b981", // Emerald
  "#f59e0b", // Amber
  "#ef4444", // Red
  "#8b5cf6", // Violet
  "#ec4899", // Pink
  "#14b8a6", // Teal
];

export const BrandingStep = ({ 
  pageTitle, 
  initialColor, 
  initialLogo, 
  onSave, 
  onSkip 
}: BrandingStepProps) => {
  const [brandColor, setBrandColor] = useState(initialColor);
  const [logoUrl, setLogoUrl] = useState(initialLogo);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please upload an image file",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please upload an image under 2MB",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/logo.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('logos')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('logos')
        .getPublicUrl(fileName);

      setLogoUrl(publicUrl);
    } catch (err: any) {
      toast({
        title: "Upload failed",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(brandColor, logoUrl);
    setSaving(false);
  };

  return (
    <div className="p-2 sm:p-4">
      {/* Header - compact */}
      <div className="text-center mb-4">
        <DisplayM className="mb-1">Brand your page</DisplayM>
        <BodyText className="text-muted-foreground text-sm">
          Add your logo and brand color to personalize your feedback page.
        </BodyText>
      </div>

      <div className="space-y-4">
        {/* Color picker */}
        <div className="space-y-2">
          <Label className="text-sm">Brand Color</Label>
          <div className="flex flex-wrap gap-2">
            {COLOR_PRESETS.map((color) => (
              <button
                key={color}
                onClick={() => setBrandColor(color)}
                className={`w-8 h-8 rounded-full border-2 transition-all ${
                  brandColor === color 
                    ? 'border-foreground scale-110' 
                    : 'border-transparent hover:scale-105'
                }`}
                style={{ backgroundColor: color }}
                aria-label={`Select color ${color}`}
              />
            ))}
            <div className="relative">
              <input
                type="color"
                value={brandColor}
                onChange={(e) => setBrandColor(e.target.value)}
                className="w-8 h-8 rounded-full cursor-pointer opacity-0 absolute inset-0"
              />
              <div 
                className="w-8 h-8 rounded-full border-2 border-dashed border-muted-foreground flex items-center justify-center"
                style={{ backgroundColor: brandColor }}
              >
                <span className="text-xs text-white drop-shadow-sm">+</span>
              </div>
            </div>
          </div>
        </div>

        {/* Logo upload - compact */}
        <div className="space-y-2">
          <Label className="text-sm">Company Logo (optional)</Label>
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <div className="w-12 h-12 rounded-lg border bg-white flex items-center justify-center overflow-hidden shrink-0">
                <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-lg border border-dashed flex items-center justify-center bg-muted/50 shrink-0">
                <Upload className="h-5 w-5 text-muted-foreground" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <Input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={uploading}
                className="cursor-pointer text-sm"
              />
              <p className="text-xs text-muted-foreground mt-0.5">PNG, JPG up to 2MB</p>
            </div>
          </div>
        </div>

        {/* Live preview - compact */}
        <div className="space-y-1.5">
          <Label className="text-sm">Preview</Label>
          <div 
            className="border rounded-lg p-3 bg-card"
            style={{ borderTopColor: brandColor, borderTopWidth: '3px' }}
          >
            <div className="flex items-center gap-2">
              {logoUrl && (
                <img src={logoUrl} alt="Logo" className="w-6 h-6 object-contain" />
              )}
              <span className="font-medium text-sm">{pageTitle}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Share your honest feedback...
            </p>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="flex gap-2 mt-5">
        <SecondaryButton onClick={onSkip} className="flex-1 min-h-[44px] text-sm">
          Skip for now
        </SecondaryButton>
        <PrimaryButton 
          onClick={handleSave} 
          className="flex-1 min-h-[44px] text-sm"
          disabled={saving || uploading}
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Saving...
            </>
          ) : (
            "Save & Continue →"
          )}
        </PrimaryButton>
      </div>
    </div>
  );
};
